import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@visionar/shared';
import { authApi, setAuthToken } from '@/lib/api-client';

// Debug logging
const logDebug = (typeof window !== 'undefined' && (window as unknown as { logDebug?: (msg: string) => void }).logDebug) || console.log.bind(console);

// Safe localStorage wrapper that handles Quest WebView issues
const safeStorage = {
  getItem: (name: string): string | null => {
    try {
      logDebug('localStorage.getItem: ' + name);
      return localStorage.getItem(name);
    } catch (e) {
      logDebug('localStorage.getItem ERROR: ' + String(e));
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      localStorage.setItem(name, value);
    } catch (e) {
      logDebug('localStorage.setItem ERROR: ' + String(e));
    }
  },
  removeItem: (name: string): void => {
    try {
      localStorage.removeItem(name);
    } catch (e) {
      logDebug('localStorage.removeItem ERROR: ' + String(e));
    }
  },
};

interface AuthState {
  user: User | null;
  sessionToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      sessionToken: null,
      isLoading: true,
      isAuthenticated: false,

      // Initialize auth token from persisted state
      initializeAuth: () => {
        const { sessionToken } = get();
        if (sessionToken) {
          setAuthToken(sessionToken);
        }
        set({ isLoading: false });
      },

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({ email, password });
          const token = response.session?.id || null;
          if (token) {
            setAuthToken(token);
          }
          set({
            user: response.user,
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email, password, name) => {
        set({ isLoading: true });
        try {
          const response = await authApi.register({ email, password, name });
          const token = response.session?.id || null;
          if (token) {
            setAuthToken(token);
          }
          set({
            user: response.user,
            sessionToken: token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authApi.logout();
        } catch {
          // Ignore errors on logout
        }
        setAuthToken(null);
        set({
          user: null,
          sessionToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      checkAuth: async () => {
        logDebug('checkAuth starting');
        const { sessionToken, user } = get();

        // If we have persisted user and token, trust it (avoid unnecessary API call)
        if (sessionToken && user) {
          logDebug('Using persisted auth');
          setAuthToken(sessionToken);
          set({ isLoading: false, isAuthenticated: true });
          return;
        }

        logDebug('No persisted auth, calling API');
        set({ isLoading: true });

        // Add timeout to prevent hanging on Quest
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Auth check timeout')), 10000)
        );

        try {
          const response = await Promise.race([authApi.me(), timeoutPromise]);
          logDebug('Auth API response received');
          set({
            user: response.user,
            isAuthenticated: !!response.user,
            isLoading: false,
          });
        } catch (error) {
          logDebug('Auth check failed: ' + String(error));
          set({
            user: null,
            sessionToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },
    }),
    {
      name: 'visionar-auth',
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({
        user: state.user,
        sessionToken: state.sessionToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrate: () => {
        logDebug('Auth store rehydrating');
        // Called when store is rehydrated from localStorage
        return (state) => {
          logDebug('Auth store rehydrated: ' + (state?.sessionToken ? 'has token' : 'no token'));
          if (state?.sessionToken) {
            setAuthToken(state.sessionToken);
          }
        };
      },
    }
  )
);
